import api from '../../../api/client';
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Navbar,
  Nav,
  Card,
  Badge,
  Button,
  Tab,
  Tabs,
} from "react-bootstrap";
import axios from "axios";
import FarmDashboard from "../FarmDashboard";
const mockFarmData = {
  farmName: "Green Valley Farms",
  manager: "Sarah Johnson",
  totalEmployees: 24,
  activeTasks: 18,
  completedTasks: 142,
  upcomingEvents: 5,
  yieldForecast: "82%",
  livestock: {
    cattle: 240,
    poultry: 1500,
    goats: 120,
    sheep: 180,
    pigs: 200,
  },
  inventory: {
    feeds: 4200,
    supplements: 350,
    eggs: 1250,
  },
  recentActivities: [
    {
      id: 1,
      action: "Harvest",
      detail: "Wheat field B-12",
      time: "2 hours ago",
    },
    {
      id: 2,
      action: "Planting",
      detail: "Corn field C-07",
      time: "5 hours ago",
    },
    {
      id: 3,
      action: "Maintenance",
      detail: "Tractor #5 repaired",
      time: "Yesterday",
    },
    {
      id: 4,
      action: "Irrigation",
      detail: "Section D watering complete",
      time: "Yesterday",
    },
  ],
  employees: [
    { id: 1, name: "Michael Chen", role: "Field Supervisor", status: "active" },
    {
      id: 2,
      name: "Jessica Williams",
      role: "Livestock Manager",
      status: "active",
    },
    {
      id: 3,
      name: "David Miller",
      role: "Equipment Operator",
      status: "on_break",
    },
    {
      id: 4,
      name: "Emily Rodriguez",
      role: "Crop Specialist",
      status: "active",
    },
    {
      id: 5,
      name: "James Wilson",
      role: "Irrigation Tech",
      status: "off_duty",
    },
  ],
};

// Mock data for animal growth stages
const growthStages = {
  poultry: [
    {
      stage: "Chicks (0-8 weeks)",
      tasks: ["Provide heat lamp", "Starter feed", "Vaccination"],
    },
    {
      stage: "Growers (9-20 weeks)",
      tasks: ["Grower feed", "Increase space", "Monitor weight"],
    },
    {
      stage: "Layers (20+ weeks)",
      tasks: ["Layer feed", "Nesting boxes", "Egg collection"],
    },
  ],
  goats: [
    {
      stage: "Kids (0-6 months)",
      tasks: ["Colostrum feeding", "Weaning", "Vaccination"],
    },
    {
      stage: "Yearlings (6-12 months)",
      tasks: ["Growth feed", "Housing separation", "Breeding prep"],
    },
    {
      stage: "Adults (1+ years)",
      tasks: ["Maintenance feed", "Breeding management", "Health checks"],
    },
  ],
  sheep: [
    {
      stage: "Lambs (0-4 months)",
      tasks: ["Milk feeding", "Weaning", "Vaccination"],
    },
    {
      stage: "Yearlings (4-12 months)",
      tasks: ["Grazing training", "Worming", "Wool care"],
    },
    {
      stage: "Adults (1+ years)",
      tasks: ["Breeding", "Shearing", "Hoof trimming"],
    },
  ],
  pigs: [
    {
      stage: "Piglets (0-8 weeks)",
      tasks: ["Heat source", "Starter feed", "Iron supplement"],
    },
    {
      stage: "Growers (8-16 weeks)",
      tasks: ["Grower feed", "Space management", "Vaccination"],
    },
    {
      stage: "Finishers (16+ weeks)",
      tasks: ["Finisher feed", "Weight monitoring", "Market prep"],
    },
  ],
};

// Mock data for animal batches with ages
const animalBatches = [
  {
    id: 1,
    type: "poultry",
    breed: "Broiler",
    count: 500,
    age: 4,
    unit: "weeks",
  },
  {
    id: 2,
    type: "poultry",
    breed: "Layer",
    count: 300,
    age: 25,
    unit: "weeks",
  },
  { id: 3, type: "goats", breed: "Boer", count: 45, age: 8, unit: "months" },
  { id: 4, type: "sheep", breed: "Dorper", count: 60, age: 14, unit: "months" },
  {
    id: 5,
    type: "pigs",
    breed: "Large White",
    count: 80,
    age: 10,
    unit: "weeks",
  },
];

function ManagerDash() {
  const [farmData, setFarmData] = useState(mockFarmData);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedAnimal, setSelectedAnimal] = useState("poultry");
  const [reminders, setReminders] = useState([]);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Generate reminders based on animal ages
  useEffect(() => {
    const generateReminders = () => {
      const newReminders = [];

      animalBatches.forEach((batch) => {
        const stages = growthStages[batch.type];

        stages.forEach((stage) => {
          const stageAgeMatch = stage.stage.match(/(\d+)[^\d]+(\d+)?/);
          if (stageAgeMatch) {
            const minAge = parseInt(stageAgeMatch[1]);
            const maxAge = stageAgeMatch[2] ? parseInt(stageAgeMatch[2]) : null;

            if (
              (batch.unit === "weeks" &&
                batch.age >= minAge &&
                (!maxAge || batch.age <= maxAge)) ||
              (batch.unit === "months" &&
                batch.age >= minAge &&
                (!maxAge || batch.age <= maxAge))
            ) {
              newReminders.push({
                batch: batch,
                stage: stage.stage,
                tasks: stage.tasks,
              });
            }
          }
        });
      });

      setReminders(newReminders);
    };

    generateReminders();
  }, []);

  const renderEmployeeStatus = (status) => {
    const statusConfig = {
      active: { variant: "success", text: "Active" },
      on_break: { variant: "warning", text: "On Break" },
      off_duty: { variant: "secondary", text: "Off Duty" },
    };
    const config = statusConfig[status] || {
      variant: "secondary",
      text: "Unknown",
    };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const renderDashboard = () => (
    <>
      {/* Farm Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>{farmData.farmName}</h2>
              <p className="text-muted">Managed by {farmData.manager}</p>
            </div>
            <div className="weather-widget p-2 bg-light rounded">
              <div className="d-flex align-items-center">
                <i className="fas fa-sun text-warning fa-2x me-2"></i>
                <div>
                  <div className="fw-bold">72°F</div>
                  <small className="text-muted">Sunny</small>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Key Metrics */}
      <Row className="mb-4">
        <Col md={6} lg={3} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <Card.Title className="text-primary">Employees</Card.Title>
                  <h3>{farmData.totalEmployees}</h3>
                  <Card.Text className="text-muted">Total on farm</Card.Text>
                </div>
                <div className="display-4 text-primary">
                  <i className="fas fa-users"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <Card.Title className="text-success">Tasks</Card.Title>
                  <h3>
                    {farmData.activeTasks}{" "}
                    <small className="text-muted">
                      / {farmData.completedTasks}
                    </small>
                  </h3>
                  <Card.Text className="text-muted">
                    Active / Completed
                  </Card.Text>
                </div>
                <div className="display-4 text-success">
                  <i className="fas fa-tasks"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <Card.Title className="text-info">Yield Forecast</Card.Title>
                  <h3>{farmData.yieldForecast}</h3>
                  <Card.Text className="text-muted">Of target</Card.Text>
                </div>
                <div className="display-4 text-info">
                  <i className="fas fa-chart-line"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <Card.Title className="text-warning">Upcoming</Card.Title>
                  <h3>{farmData.upcomingEvents}</h3>
                  <Card.Text className="text-muted">Events this week</Card.Text>
                </div>
                <div className="display-4 text-warning">
                  <i className="fas fa-calendar-day"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Recent Activity */}
        <Col lg={8} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Recent Activity</h5>
            </Card.Header>
            <Card.Body>
              <div className="activity-feed">
                {farmData.recentActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className="feed-item p-3 border-start border-3 border-success"
                  >
                    <div className="d-flex justify-content-between">
                      <div>
                        <h6 className="mb-0">{activity.action}</h6>
                        <p className="mb-0 text-muted">{activity.detail}</p>
                      </div>
                      <span className="text-muted">{activity.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Employee Status */}
        <Col lg={4} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Employee Status</h5>
              <Button variant="outline-primary" size="sm">
                View All
              </Button>
            </Card.Header>
            <Card.Body>
              {farmData.employees.map((employee) => (
                <div
                  key={employee.id}
                  className="employee-item d-flex justify-content-between align-items-center mb-3 p-2 bg-light rounded"
                >
                  <div>
                    <h6 className="mb-0">{employee.name}</h6>
                    <small className="text-muted">{employee.role}</small>
                  </div>
                  {renderEmployeeStatus(employee.status)}
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Livestock Overview */}
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Livestock Overview</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4} className="text-center mb-3">
                  <div className="p-3 bg-light rounded">
                    <i className="fas fa-cow fa-2x text-primary mb-2"></i>
                    <h4>Cattle</h4>
                    <h2 className="text-primary">
                      {farmData.livestock.cattle}
                    </h2>
                  </div>
                </Col>
                <Col md={4} className="text-center mb-3">
                  <div className="p-3 bg-light rounded">
                    <i className="fas fa-egg fa-2x text-warning mb-2"></i>
                    <h4>Poultry</h4>
                    <h2 className="text-warning">
                      {farmData.livestock.poultry}
                    </h2>
                  </div>
                </Col>
                <Col md={4} className="text-center mb-3">
                  <div className="p-3 bg-light rounded">
                    <i className="fas fa-cloud-meatball fa-2x text-info mb-2"></i>
                    <h4>Goats</h4>
                    <h2 className="text-info">{farmData.livestock.goats}</h2>
                  </div>
                </Col>
                <Col md={4} className="text-center mb-3">
                  <div className="p-3 bg-light rounded">
                    <i className="fas fa-cloud-meatball fa-2x text-success mb-2"></i>
                    <h4>Sheep</h4>
                    <h2 className="text-success">{farmData.livestock.sheep}</h2>
                  </div>
                </Col>
                <Col md={4} className="text-center mb-3">
                  <div className="p-3 bg-light rounded">
                    <i className="fas fa-piggy-bank fa-2x text-danger mb-2"></i>
                    <h4>Pigs</h4>
                    <h2 className="text-danger">{farmData.livestock.pigs}</h2>
                  </div>
                </Col>
                <Col md={4} className="text-center mb-3">
                  <div className="p-3 bg-light rounded">
                    <i className="fas fa-egg fa-2x text-secondary mb-2"></i>
                    <h4>Egg Inventory</h4>
                    <h2 className="text-secondary">
                      {farmData.inventory.eggs}
                    </h2>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  const renderAnimalManagement = () => (
    <div>
      <h2 className="mb-4">Animal Management</h2>

      <Row className="mb-4">
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Feed Inventory</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded mb-3">
                <div>
                  <h6 className="mb-0">Animal Feed</h6>
                  <small className="text-muted">Total bags in stock</small>
                </div>
                <h4 className="text-primary mb-0">
                  {farmData.inventory.feeds}
                </h4>
              </div>
              <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                <div>
                  <h6 className="mb-0">Supplements</h6>
                  <small className="text-muted">Total containers</small>
                </div>
                <h4 className="text-warning mb-0">
                  {farmData.inventory.supplements}
                </h4>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Recent Transactions</h5>
            </Card.Header>
            <Card.Body>
              <div className="transaction-item p-3 border-start border-3 border-info mb-2">
                <div className="d-flex justify-content-between">
                  <div>
                    <h6 className="mb-0">Feed Purchase</h6>
                    <small className="text-muted">
                      500 bags of poultry feed
                    </small>
                  </div>
                  <div className="text-end">
                    <div className="fw-bold">$2,500</div>
                    <small className="text-muted">2 days ago</small>
                  </div>
                </div>
              </div>
              <div className="transaction-item p-3 border-start border-3 border-success">
                <div className="d-flex justify-content-between">
                  <div>
                    <h6 className="mb-0">Egg Sales</h6>
                    <small className="text-muted">200 crates to market</small>
                  </div>
                  <div className="text-end">
                    <div className="fw-bold">$1,800</div>
                    <small className="text-muted">1 day ago</small>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <Card.Header>
          <h5 className="mb-0">Animal Batches</h5>
        </Card.Header>
        <Card.Body>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Breed</th>
                  <th>Count</th>
                  <th>Age</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {animalBatches.map((batch) => (
                  <tr key={batch.id}>
                    <td>
                      <i
                        className={`fas fa-${
                          batch.type === "poultry"
                            ? "egg"
                            : batch.type === "goats"
                            ? "cloud-meatball"
                            : batch.type === "sheep"
                            ? "cloud-meatball"
                            : "piggy-bank"
                        } me-2`}
                      ></i>
                      {batch.type.charAt(0).toUpperCase() + batch.type.slice(1)}
                    </td>
                    <td>{batch.breed}</td>
                    <td>{batch.count}</td>
                    <td>
                      {batch.age} {batch.unit}
                    </td>
                    <td>
                      <Badge bg={batch.age < 10 ? "success" : "warning"}>
                        {batch.age < 10 ? "Growing" : "Mature"}
                      </Badge>
                    </td>
                    <td>
                      <Button variant="outline-primary" size="sm">
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  const renderGrowthCycle = () => (
    <div>
      <h2 className="mb-4">Animal Growth Cycle Management</h2>

      <Row className="mb-4">
        <Col md={8}>
          <Card className="shadow-sm mb-4">
            <Card.Header>
              <h5 className="mb-0">Growth Stages</h5>
            </Card.Header>
            <Card.Body>
              <Tabs
                activeKey={selectedAnimal}
                onSelect={(k) => setSelectedAnimal(k)}
                className="mb-3"
              >
                <Tab eventKey="poultry" title="Poultry">
                  <div className="p-3">
                    <h5>Poultry Growth Stages</h5>
                    {growthStages.poultry.map((stage, index) => (
                      <div
                        key={index}
                        className="stage-card p-3 bg-light rounded mb-3"
                      >
                        <h6>{stage.stage}</h6>
                        <ul className="mb-0">
                          {stage.tasks.map((task, i) => (
                            <li key={i}>{task}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </Tab>
                <Tab eventKey="goats" title="Goats">
                  <div className="p-3">
                    <h5>Goats Growth Stages</h5>
                    {growthStages.goats.map((stage, index) => (
                      <div
                        key={index}
                        className="stage-card p-3 bg-light rounded mb-3"
                      >
                        <h6>{stage.stage}</h6>
                        <ul className="mb-0">
                          {stage.tasks.map((task, i) => (
                            <li key={i}>{task}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </Tab>
                <Tab eventKey="sheep" title="Sheep">
                  <div className="p-3">
                    <h5>Sheep Growth Stages</h5>
                    {growthStages.sheep.map((stage, index) => (
                      <div
                        key={index}
                        className="stage-card p-3 bg-light rounded mb-3"
                      >
                        <h6>{stage.stage}</h6>
                        <ul className="mb-0">
                          {stage.tasks.map((task, i) => (
                            <li key={i}>{task}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </Tab>
                <Tab eventKey="pigs" title="Pigs">
                  <div className="p-3">
                    <h5>Pigs Growth Stages</h5>
                    {growthStages.pigs.map((stage, index) => (
                      <div
                        key={index}
                        className="stage-card p-3 bg-light rounded mb-3"
                      >
                        <h6>{stage.stage}</h6>
                        <ul className="mb-0">
                          {stage.tasks.map((task, i) => (
                            <li key={i}>{task}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Growth Reminders</h5>
            </Card.Header>
            <Card.Body>
              {reminders.length > 0 ? (
                reminders.map((reminder, index) => (
                  <div
                    key={index}
                    className="reminder-item p-3 border-start border-3 border-warning mb-3"
                  >
                    <h6>
                      {reminder.batch.breed} {reminder.batch.type} (
                      {reminder.batch.count} animals)
                    </h6>
                    <p className="mb-1">
                      Current age: {reminder.batch.age} {reminder.batch.unit}
                    </p>
                    <p className="mb-1">Stage: {reminder.stage}</p>
                    <div>
                      <strong>Tasks:</strong>
                      <ul className="mb-0">
                        {reminder.tasks.map((task, i) => (
                          <li key={i}>{task}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted">No reminders at this time.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );

  return (
    <div className="App">
      {/* <Navbar bg="dark" variant="dark" expand="lg" className="px-3">
        <Navbar.Brand href="#">
          <i className="fas fa-tractor me-2"></i>
          Farm Manager Dashboard
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="me-auto">
            <Nav.Link
              active={activeTab === "dashboard"}
              onClick={() => setActiveTab("dashboard")}
            >
              <i className="fas fa-chart-line me-1"></i>
              Dashboard
            </Nav.Link>
            <Nav.Link
              active={activeTab === "animals"}
              onClick={() => setActiveTab("animals")}
            >
              <i className="fas fa-cloud-meatball me-1"></i>
              Animal Management
            </Nav.Link>
            <Nav.Link
              active={activeTab === "growth"}
              onClick={() => setActiveTab("growth")}
            >
              <i className="fas fa-seedling me-1"></i>
              Growth Cycle
            </Nav.Link>
          </Nav>
          <Navbar.Text className="justify-content-end">
            <i className="far fa-clock me-1"></i>
            {currentTime.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
            <span className="ms-2">{currentTime.toLocaleDateString()}</span>
          </Navbar.Text>
        </Navbar.Collapse>
      </Navbar>

      <Container fluid className="mt-4">
        {activeTab === "dashboard" && renderDashboard()}
        {activeTab === "animals" && renderAnimalManagement()}
        {activeTab === "growth" && renderGrowthCycle()}
      </Container>

      <footer className="mt-5 py-3 bg-light text-center">
        <p className="mb-0">
          Farm Manager Dashboard v1.0 • {new Date().getFullYear()}
        </p>
      </footer> */}
      <FarmDashboard />
    </div>
  );
}

export default ManagerDash;
